import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { client } from '../generated/client.gen';
import { segmentsQueryKey } from './workspace';

export type JobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export type SyncJobType =
  | 'segment_to_brevo'
  | 'segment_desync_brevo'
  | 'segment_forward_brevo'
  | 'segment_to_ecomail'
  | 'segment_desync_ecomail'
  | 'segment_forward_ecomail'
  | 'segment_to_mailchimp'
  | 'segment_desync_mailchimp'
  | 'segment_forward_mailchimp';

export interface SyncJob {
  id: string;
  type: SyncJobType;
  segmentId: string;
  integrationId: string;
  ownerId?: string;
  status: JobStatus;
  total: number;
  processed: number;
  added: number;
  updated: number;
  removed: number;
  deleted: number;
  failed: number;
  error?: string;
  workerId?: string;
  startedAt?: string;
  finishedAt?: string;
  heartbeatAt?: string;
  createdAt: string;
  resultListId?: string;
}

export function isJobActive(status: JobStatus): boolean {
  return status === 'queued' || status === 'running';
}

export function isDesyncJob(type: string): boolean {
  return type.startsWith('segment_desync_');
}

export function isForwardJob(type: string): boolean {
  return type.startsWith('segment_forward_');
}

export function jobProviderLabel(type: string): string {
  const provider = type.split('_').pop() ?? '';
  return provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'the mailing service';
}

const syncJobKey = (id: string) => ['sync-jobs', id] as const;
const activeJobKey = (segmentId: string) => ['segments', segmentId, 'active-sync-job'] as const;
export const activeSyncJobsQueryKey = (segmentId: string) =>
  ['segments', segmentId, 'active-sync-jobs'] as const;

const ACTIVE_POLL_MS = 2000;
const IDLE_POLL_MS = 60000;

function isSyncJob(value: unknown): value is SyncJob {
  return !!value && typeof value === 'object' && typeof (value as SyncJob).id === 'string';
}

function trackStartedJob(queryClient: QueryClient, segmentId: string, job: SyncJob) {
  queryClient.setQueryData(syncJobKey(job.id), job);
  queryClient.setQueryData(activeJobKey(segmentId), job);
  queryClient.setQueryData<SyncJob[]>(activeSyncJobsQueryKey(segmentId), (old) => [
    ...(old ?? []).filter((j) => j.id !== job.id),
    job,
  ]);
}

async function fetchJobOrNull(id: string): Promise<SyncJob | null> {
  try {
    const { data } = await client.instance.get<SyncJob>(`/sync-jobs/${id}`);
    return isSyncJob(data) ? data : null;
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 404) return null;
    throw e;
  }
}

async function fetchActiveJobs(segmentId: string): Promise<SyncJob[]> {
  try {
    const { data } = await client.instance.get<{ data?: SyncJob[] } | SyncJob[]>(
      `/segments/${segmentId}/active-sync-jobs`,
    );
    const list = Array.isArray(data) ? data : (data?.data ?? []);
    return list.filter(isSyncJob);
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 404) return [];
    throw e;
  }
}

function useStartSegmentSync(segmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (integrationId: string): Promise<SyncJob> => {
      const { data } = await client.instance.post<SyncJob>(
        `/segments/${segmentId}/sync`,
        { integrationId },
      );
      return data;
    },
    onSuccess: (job) => {
      trackStartedJob(queryClient, segmentId, job);
      return queryClient.invalidateQueries({ queryKey: segmentsQueryKey });
    },
  });
}

function useStartSegmentDesync(segmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (integrationId: string): Promise<SyncJob> => {
      const { data } = await client.instance.post<SyncJob>(
        `/segments/${segmentId}/desync`,
        { integrationId },
      );
      return data;
    },
    onSuccess: (job) => {
      trackStartedJob(queryClient, segmentId, job);
    },
  });
}

function useSyncJob(jobId: string | undefined) {
  return useQuery({
    queryKey: jobId ? syncJobKey(jobId) : ['sync-jobs', 'idle'],
    enabled: !!jobId,
    queryFn: async (): Promise<SyncJob> => {
      const { data } = await client.instance.get<SyncJob>(`/sync-jobs/${jobId}`);
      return data;
    },
    refetchInterval: (query) => {
      const data = query.state.data as SyncJob | undefined;
      if (!data) return ACTIVE_POLL_MS;
      return isJobActive(data.status) ? ACTIVE_POLL_MS : false;
    },
    staleTime: 0,
  });
}

function useActiveSyncJob(segmentId: string) {
  return useQuery({
    queryKey: activeJobKey(segmentId),
    queryFn: async (): Promise<SyncJob | null> => {
      try {
        const { data } = await client.instance.get<SyncJob | ''>(
          `/segments/${segmentId}/active-sync-job`,
        );
        return isSyncJob(data) ? data : null;
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return null;
        throw e;
      }
    },
    refetchInterval: (query) => {
      const data = query.state.data as SyncJob | null | undefined;
      if (data && isJobActive(data.status)) return ACTIVE_POLL_MS;
      return false;
    },
    staleTime: 0,
  });
}

function useActiveSyncJobs(segmentId: string, options: { pollWhenIdle?: boolean } = {}) {
  const pollWhenIdle = options.pollWhenIdle ?? false;
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: activeSyncJobsQueryKey(segmentId),
    queryFn: async (): Promise<SyncJob[]> => {
      const previous =
        queryClient.getQueryData<SyncJob[]>(activeSyncJobsQueryKey(segmentId)) ?? [];
      const active = await fetchActiveJobs(segmentId);
      const activeIds = new Set(active.map((j) => j.id));
      const vanished = previous.filter((j) => isJobActive(j.status) && !activeIds.has(j.id));
      if (vanished.length === 0) return active;

      const finished = await Promise.all(vanished.map((j) => fetchJobOrNull(j.id)));
      void queryClient.invalidateQueries({ queryKey: segmentsQueryKey });
      return [
        ...active,
        ...finished.filter((j): j is SyncJob => !!j && !isJobActive(j.status)),
      ];
    },
    refetchInterval: (query) => {
      if (query.state.data?.some((j) => isJobActive(j.status))) return ACTIVE_POLL_MS;
      return pollWhenIdle ? IDLE_POLL_MS : false;
    },
    staleTime: 0,
  });
}

export const syncJobsApi = {
  useStartSegmentSync,
  useStartSegmentDesync,
  useSyncJob,
  useActiveSyncJob,
  useActiveSyncJobs,
};
